// Classifies a mapping draft's preview result into one of the three states
// docs/live-mapping-task.md's LiveMapping card renders: `resolved` (nothing
// left to decide), `needs-decision` (one specific gap), or `no-sample`
// (nothing loaded yet). This is a NEW classification layer over the exact
// preview the Advanced editor's Live preview pane already computes
// (mappingPreview.ts's previewMapping) - not a new evaluation path, so the
// two views can never disagree with each other.
import type { JsonValue, MappingRule, MappingSpec } from './mappingEngine';
import type { PreviewRecordResult } from './mappingPreview';
import { getOutputs } from './mappingSpecEdit';
import { distinctValues } from './signalSplit';

export interface UnmappedValuesGap {
  kind: 'unmapped-values';
  outputIndex: number;
  ruleIndex: number;
  field: string;
  values: string[]; // distinct values seen with no rule.map entry yet
}

export interface RequiredEmptyGap {
  kind: 'required-empty';
  outputIndex: number;
  field: string;
}

export type MappingGap = UnmappedValuesGap | RequiredEmptyGap;

export type MappingClassification = { state: 'no-sample' } | { state: 'resolved' } | { state: 'needs-decision'; gap: MappingGap };

// The one `name`/`signalRef` rule (per output) that reads a record field -
// the only rule shape a single flat pick can't cover for every distinct
// value it sees (see signalSplit.ts's header comment: this is the exact
// shape `rule.map` already resolves per-value, generically, in every engine).
export function findSplitRule(rules: MappingRule[]): { rule: MappingRule; index: number } | null {
  for (let i = 0; i < rules.length; i++) {
    const r = rules[i];
    if ((r.to === 'name' || r.to === 'signalRef') && r.from) return { rule: r, index: i };
  }
  return null;
}

// Every distinct value of that rule's source field, whether or not it
// already has a `rule.map` entry - LiveMapping's row list shows one row per
// value regardless of state; only the unmapped subset feeds the gap below.
export function splitValuesFor(rules: MappingRule[], records: JsonValue[]): { field: string; ruleIndex: number; values: string[] } | null {
  const found = findSplitRule(rules);
  if (!found || !found.rule.from) return null;
  const values = distinctValues(records, found.rule.from);
  if (values.length === 0) return null;
  return { field: found.rule.from, ruleIndex: found.index, values };
}

export function classifyMapping(spec: MappingSpec, records: JsonValue[], results: PreviewRecordResult[] | null): MappingClassification {
  if (records.length === 0 || !results) return { state: 'no-sample' };

  const outputs = getOutputs(spec);

  // A `required` rule that dropped the whole Signal on some record: the one
  // state the card refuses to paper over with a confident-looking button
  // (docs/live-mapping-task.md's worked "Fix amount" example).
  for (let o = 0; o < outputs.length; o++) {
    const droppedHere = results.some((r) => r.outputs[o]?.status === 'dropped-required');
    if (!droppedHere) continue;
    const requiredRule = outputs[o].find((r) => r.required && r.to);
    if (requiredRule?.to) return { state: 'needs-decision', gap: { kind: 'required-empty', outputIndex: o, field: requiredRule.to } };
  }

  for (let o = 0; o < outputs.length; o++) {
    const split = splitValuesFor(outputs[o], records);
    if (!split || split.values.length <= 1) continue;
    const rule = outputs[o][split.ruleIndex];
    const unmapped = split.values.filter((v) => !rule.map || rule.map[v] === undefined);
    if (unmapped.length > 0) {
      return { state: 'needs-decision', gap: { kind: 'unmapped-values', outputIndex: o, ruleIndex: split.ruleIndex, field: split.field, values: unmapped } };
    }
  }

  return { state: 'resolved' };
}
